// src/pages/DateRangeReport.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress
} from '@mui/material';
import { Refresh as RefreshIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import { apiUrl } from '../config/api';
import AppDateField from '../components/inputs/AppDateField';

const API_URL = apiUrl('date_range_report');
const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const normalizeReport = (payload) => ({
  total_card_payments: asNumber(payload?.total_card_payments),
  total_cash_payments: asNumber(payload?.total_cash_payments),
  total_online_sales: asNumber(payload?.total_online_sales),
  total_credit_sales: asNumber(payload?.total_credit_sales),
  total_tips: asNumber(payload?.total_tips),
  total_service_charges: asNumber(payload?.total_service_charges),
  total_subtotal: asNumber(payload?.total_subtotal),
});

const DateRangeReport = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = () => {
    if (!startDate || !endDate) {
      alert("Please provide both start and end dates.");
      return;
    }
    setLoading(true);
    fetch(`${API_URL}?start_date=${startDate}&end_date=${endDate}`)
      .then(res => res.json())
      .then(data => {
        if (data?.status === 'error') {
          setReport(null);
          alert(data?.message || 'Failed to load report');
          return;
        }
        setReport(normalizeReport(data));
      })
      .catch(err => {
        console.error("Error fetching report", err);
      })
      .finally(() => setLoading(false));
  };

  // Download CSV function
  const downloadCSV = () => {
    if (!report) return;
    const headers = [
      "Metric", 
      "Value"
    ];
    const rows = [
      ["Total Card Payments", report.total_card_payments.toFixed(2)],
      ["Total Cash Payments", report.total_cash_payments.toFixed(2)],
      ["Total Online Sales", report.total_online_sales.toFixed(2)],
      ["Total Credit Sales", report.total_credit_sales.toFixed(2)],
      ["Total Tips", report.total_tips.toFixed(2)],
      ["Total Service Charges", report.total_service_charges.toFixed(2)],
      ["Grand Total", report.total_subtotal.toFixed(2)]
    ];

    let csvContent = headers.join(",") + "\n";
    rows.forEach(row => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `date_range_report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Date Range Summary Report
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <AppDateField 
            label="Start Date" 
            fullWidth 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
            helperText="DD/MM/YYYY"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <AppDateField 
            label="End Date" 
            fullWidth 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
            helperText="DD/MM/YYYY"
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button 
            variant="contained" 
            onClick={fetchReport} 
            fullWidth 
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Generate
          </Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          {report && (
            <Button 
              variant="outlined" 
              onClick={downloadCSV} 
              fullWidth 
              startIcon={<GetAppIcon />}
            >
              Download
            </Button>
          )}
        </Grid>
      </Grid>
      {loading && <CircularProgress />}
      {report && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Card Payments</Typography>
                <Typography variant="h5">{report.total_card_payments.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Cash Payments</Typography>
                <Typography variant="h5">{report.total_cash_payments.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Online Sales</Typography>
                <Typography variant="h5">{report.total_online_sales.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Credit Sales</Typography>
                <Typography variant="h5">{report.total_credit_sales.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Tips</Typography>
                <Typography variant="h5">{report.total_tips.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Total Service Charges</Typography>
                <Typography variant="h5">{report.total_service_charges.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">Grand Total (Subtotal)</Typography>
                <Typography variant="h4">{report.total_subtotal.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DateRangeReport;
